import { useState, useRef, useCallback, useEffect } from 'react';
import { parseContentWithCode } from '../utils/parseContentWithCode';
import { logAssistantEvent } from '../utils/assistantTelemetry';

const SpeechRecognitionAPI =
  typeof window !== 'undefined' &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

export function useSpeechRecognitionSupported() {
  return !!SpeechRecognitionAPI;
}

function getMaleVoice() {
  const voices = window.speechSynthesis ? window.speechSynthesis.getVoices() : [];
  const fr = voices.find(function (v) {
    return v.lang.startsWith('fr') && !v.name.toLowerCase().includes('female');
  });
  if (fr) return fr;
  const anyFr = voices.find(function (v) { return v.lang.startsWith('fr'); });
  if (anyFr) return anyFr;
  return voices[0];
}

function sanitizeMarkdownForSpeech(rawText) {
  if (!rawText) return '';
  var text = String(rawText);

  // Si le contenu est coupé au milieu d'un bloc de code triple-backtick (cas streaming),
  // on évite de vocaliser des morceaux incohérents.
  var fenceCount = (text.match(/```/g) || []).length;

  // Liens markdown : [libellé](url) -> libellé (ne pas vocaliser les crochets)
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');

  // Inline code/backticks : on évite de vocaliser les fragments de code
  // (ex: `walletAddress` -> "code")
  text = text.replace(/`([^`]+)`/g, ' code ');

  // Mise en forme markdown simple : **gras** / *italique* -> texte
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/\*([^*]+)\*/g, '$1');

  // Titres markdown : ### Titre -> Titre
  text = text.replace(/^\s*#{1,6}\s+/gm, '');

  // Listes markdown : "- item" / "1. item" -> item
  text = text.replace(/^\s*[-*+]\s+/gm, '');
  text = text.replace(/^\s*\d+\.\s+/gm, '');

  // Lignes de citations : "> quote" -> quote
  text = text.replace(/^\s*>\s?/gm, '');

  // Nettoyage des symboles typiques qu'on ne veut pas entendre
  text = text.replace(/[{}[\]]/g, ' ');
  text = text.replace(/\\+/g, ' ');
  text = text.replace(/```/g, ' ');

  // Normaliser les espaces
  text = text.replace(/\s{2,}/g, ' ').trim();
  return { text: text, fenceCount: fenceCount };
}

function extractHasCode(parts) {
  return parts.some(function (p) { return p.type === 'code'; });
}

function getTextOnlyForTTS(content, opts) {
  if (!content) return '';
  var options = opts || {};
  var rawText = String(content || '');

  const parts = parseContentWithCode(rawText);
  var hasCodeInFences = extractHasCode(parts);
  var hasCode = Boolean(options.hasCode || hasCodeInFences);

  // Si le markdown est "ouvert" (ex: streaming en plein milieu d'un bloc ```),
  // on évite de parler sauf si force = true.
  var { text: joinedRawText, fenceCount } = (function () {
    var textParts = parts
      .filter(function (p) { return p.type === 'text'; })
      .map(function (p) { return p.value; });
    var joined = textParts.join(' ');
    var sanitized = sanitizeMarkdownForSpeech(joined);
    return { text: sanitized.text, fenceCount: sanitized.fenceCount };
  })();

  if (!options.force && (fenceCount % 2 === 1)) {
    // Si le markdown est "ouvert" (stream au milieu d'un ```),
    // on évite de parler uniquement s'il ne reste aucun texte "hors code".
    if (!(joinedRawText || '').trim()) return '';
  }

  var text = joinedRawText;
  if (!text) return '';

  // Quand on a du code à afficher (snippets / fences), on ne lit pas le code :
  // on ajoute plutôt une phrase d'explication pour l'utilisateur.
  if (hasCode) {
    var alreadyExplains = /(permet|sert\s*à|valider|v[eé]rifier|g[eé]n[eé]rer|authentifier|payer|wallet)/i.test(text);
    if (alreadyExplains) {
      text += ' Le code correspondant est affiché à l’écran.';
    } else {
      text += " Le code que je vous présente est affiché à l’écran. Pour les détails, lisez-le visuellement.";
    }
  }

  return text || '';
}

export function useVoiceMode(options) {
  const onTranscription = options && options.onTranscription;
  const onSpeakStart = options && options.onSpeakStart;
  const onSpeakEnd = options && options.onSpeakEnd;
  const onTranscriptionRef = useRef(onTranscription);
  const onSpeakStartRef = useRef(onSpeakStart);
  const onSpeakEndRef = useRef(onSpeakEnd);
  onTranscriptionRef.current = onTranscription;
  onSpeakStartRef.current = onSpeakStart;
  onSpeakEndRef.current = onSpeakEnd;

  const [isListening, setIsListening] = useState(false);
  const [permissionState, setPermissionState] = useState('prompt');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const ttsSeqRef = useRef(0); // Invalide les callbacks TTS des énoncés annulés

  const initRecognition = useCallback(function () {
    if (!SpeechRecognitionAPI) return null;
    const Recognition = SpeechRecognitionAPI;
    const r = new Recognition();
    // On évite le "décrochage" trop tôt : on garde la reco active
    // et on émet la transcription après une période de silence.
    r.continuous = true;
    r.interimResults = true;
    r.lang = 'fr-FR';
    var finalTranscript = '';
    var interimTranscript = '';
    var latestTranscript = '';
    var silenceTimer = null;
    var SILENCE_MS = 1700;
    var hasEmittedForCurrentUtterance = false;

    function clearSilenceTimer() {
      if (silenceTimer) clearTimeout(silenceTimer);
      silenceTimer = null;
    }

    function scheduleEmit() {
      clearSilenceTimer();
      silenceTimer = setTimeout(function () {
        if (hasEmittedForCurrentUtterance) return;
        var txt = (latestTranscript || '').trim();
        if (!txt) return;
        hasEmittedForCurrentUtterance = true;
        latestTranscript = '';
        finalTranscript = '';
        interimTranscript = '';
        clearSilenceTimer();
        setIsListening(false);
        logAssistantEvent('voice_transcription', { length: txt.length });
        if (onTranscriptionRef.current) onTranscriptionRef.current(txt);
        try { r.stop(); } catch (e) {}
      }, SILENCE_MS);
    }

    r.onresult = function (e) {
      // N'émet pas au "final" immédiat : on attend le silence pour que l'utilisateur ait fini.
      hasEmittedForCurrentUtterance = false;
      var results = e && e.results ? Array.from(e.results) : [];
      // On reconstruit le texte final + le "last interim".
      finalTranscript = '';
      interimTranscript = '';
      for (var i = 0; i < results.length; i++) {
        var res = results[i];
        if (!res || !res[0]) continue;
        var t = res[0].transcript || '';
        if (!t) continue;
        if (res.isFinal) finalTranscript = (finalTranscript + ' ' + t).trim();
        else interimTranscript = t;
      }
      latestTranscript = (finalTranscript + ' ' + interimTranscript).trim();
      if (latestTranscript) scheduleEmit();
    };

    r.onend = function () {
      clearSilenceTimer();
      hasEmittedForCurrentUtterance = false;
      latestTranscript = '';
      finalTranscript = '';
      interimTranscript = '';
      logAssistantEvent('voice_listen_end', {});
      setIsListening(false);
    };
    r.onerror = function (e) {
      clearSilenceTimer();
      logAssistantEvent('voice_recognition_error', {
        error: e && e.error ? e.error : 'unknown',
      });
      if (e.error === 'not-allowed') setPermissionState('denied');
      setIsListening(false);
    };
    return r;
  }, []);

  const startListening = useCallback(function () {
    if (!SpeechRecognitionAPI) return;
    // Ne démarre pas la reconnaissance pendant un TTS en cours.
    if (window.speechSynthesis && (window.speechSynthesis.speaking || window.speechSynthesis.paused)) {
      return;
    }
    if (!recognitionRef.current) recognitionRef.current = initRecognition();
    const r = recognitionRef.current;
    if (!r) return;
    try {
      r.start();
      setIsListening(true);
      setPermissionState('granted');
      logAssistantEvent('voice_listen_start', {});
    } catch (err) {
      logAssistantEvent('voice_listen_start_error', {
        message: err && err.message ? err.message : 'unknown',
      });
      if (err && err.message && err.message.includes('not-allowed')) setPermissionState('denied');
    }
  }, [initRecognition]);

  const stopListening = useCallback(function () {
    if (recognitionRef.current && isListening) {
      try { recognitionRef.current.stop(); } catch (e) {}
    }
    setIsListening(false);
  }, [isListening]);

  const speakText = useCallback(function (content, ttsOptions) {
    const text = getTextOnlyForTTS(content, ttsOptions);
    if (!text) { if (onSpeakEnd) onSpeakEnd(); return; }
    ttsSeqRef.current += 1;
    const mySeq = ttsSeqRef.current;
    var cancelExisting = ttsOptions && ttsOptions.cancel === false ? false : true;
    if (cancelExisting && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      logAssistantEvent('tts_interruption', { reason: 'cancel_before_speak' });
    }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR';
    u.rate = 1;
    const voice = getMaleVoice();
    if (voice) u.voice = voice;
    u.onstart = function () {
      if (mySeq !== ttsSeqRef.current) return;
      setIsSpeaking(true);
      logAssistantEvent('tts_start', { length: text.length });
      if (onSpeakStartRef.current) onSpeakStartRef.current();
    };
    u.onend = function () {
      if (mySeq !== ttsSeqRef.current) return;
      setIsSpeaking(false);
      logAssistantEvent('tts_end', {});
      if (onSpeakEndRef.current) onSpeakEndRef.current();
    };
    u.onerror = function () {
      if (mySeq !== ttsSeqRef.current) return;
      setIsSpeaking(false);
      logAssistantEvent('tts_error', {});
      if (onSpeakEndRef.current) onSpeakEndRef.current();
    };
    // Met à jour l'état immédiatement pour éviter un "faux intervalle" où
    // l'UI afficherait encore "En écoute" pendant que le navigateur démarre le TTS.
    if (mySeq === ttsSeqRef.current) setIsSpeaking(true);
    if (window.speechSynthesis) window.speechSynthesis.speak(u);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refs used for callbacks
  }, []);

  const stopSpeaking = useCallback(function () {
    ttsSeqRef.current += 1; // invalide les callbacks onend des énoncés en cours
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIsSpeaking(false);
    logAssistantEvent('tts_interruption', { reason: 'manual_stop' });
    // Ne déclenche pas onSpeakEnd : éviter les bascules écoute/parole dues aux annulations.
  }, []);

  useEffect(function () {
    function loadVoices() {
      if (window.speechSynthesis) window.speechSynthesis.getVoices();
    }
    loadVoices();
    if (window.speechSynthesis && window.speechSynthesis.addEventListener) {
      window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    }
    return function () {
      if (window.speechSynthesis && window.speechSynthesis.removeEventListener) {
        window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      }
    };
  }, []);

  const requestPermission = useCallback(async function () {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (stream) {
        stream.getTracks().forEach(function (t) { t.stop(); });
        setPermissionState('granted');
        logAssistantEvent('voice_permission_granted', {});
      }
    } catch (e) {
      setPermissionState('denied');
      logAssistantEvent('voice_permission_denied', {});
    }
  }, []);

  return {
    isListening,
    isSpeaking,
    permissionState,
    supported: !!SpeechRecognitionAPI,
    startListening,
    stopListening,
    speakText,
    stopSpeaking,
    requestPermission,
    getTextOnlyForTTS,
  };
}
